<script setup>
import { ref, computed, watch } from 'vue';
import { Head, router, usePage } from '@inertiajs/vue3';
import {
    PhUserCirclePlus, PhToggleLeft, PhToggleRight,
    PhEye, PhProhibit, PhCheck,
} from '@phosphor-icons/vue';
import Layout    from '@pages/Shared/layouts/AdminLayout.vue';
import Table     from '@pages/Shared/ui/Table.vue';
import { flashToast } from '@/toast.js';
import userMale   from '@/../images/user-male.png';
import userFemale from '@/../images/user-female.png';

const props = defineProps({
    applicants:    Object,
    sessions:      Array,
    activeSession: Object,
    filters:       Object,
});

const filters = ref({ search: null, is_active: null, ...props.filters });
watch(() => props.filters, val => { filters.value = { ...val }; }, { deep: true });

const applyFilters = () => {
    const cleaned = Object.fromEntries(
        Object.entries(filters.value).filter(([_, v]) => v !== null && v !== undefined && v !== '')
    );
    router.get('/admin/applicants', { ...cleaned }, {
        preserveState: true, preserveScroll: true, replace: true,
    });
};

const handleSearch = (value) => { filters.value.search = value; applyFilters(); };
const handleFilter = (newFilters) => { filters.value = { ...filters.value, ...newFilters }; applyFilters(); };

const toggleActive = (id, currentStatus) => {
    router.patch(`/admin/applicants/${id}/toggle-active`, {}, {
        preserveScroll: true,
        onSuccess: (page) => flashToast(page.props.flash),
    });
};

const page = usePage();
const can  = computed(() => page.props.can || {});

const columns = [
    { key: 'index',       label: '#',         type: 'index'   },
    { key: 'id',          label: 'ID'                          },
    { key: 'avatar',      label: 'Avatar',    type: 'slot'    },
    { key: 'name',        label: 'Name',      type: 'slot'    },
    { key: 'email',       label: 'Email'                       },
    { key: 'phone',       label: 'Phone'                       },
    { key: 'gender',      label: 'Gender'                      },
    { key: 'application', label: 'Application', type: 'slot'  },
    { key: 'status',      label: 'Status',    type: 'slot'    },
    { key: 'actions',     label: '',          type: 'actions' },
];
</script>

<template>
    <Layout>
        <Head title="Applicants" />

        <!-- Header -->
        <div class="flex items-center justify-between mb-6">
            <div>
                <h2 class="text-xl font-bold text-primary">Applicants</h2>
                <p class="text-sm text-gray-500 mt-0.5">All registered applicant portal accounts</p>
            </div>
        </div>

        <Table
            :data="applicants"
            :columns="columns"
            row-key="id"
            :exportable="true"
            :actions="{ view: true }"
            :filters="[
                {
                    key: 'is_active', label: 'Status',
                    options: [
                        { label: 'All Statuses', value: null   },
                        { label: 'Active',       value: '1'    },
                        { label: 'Inactive',     value: '0'    },
                    ],
                    value: filters.is_active,
                },
            ]"
            @search="handleSearch"
            @filter="handleFilter"
            @view="(id) => router.get(`/admin/applicants/${id}`)"
        >
            <!-- Avatar -->
            <template #avatar="{ row }">
                <img
                    class="w-9 h-9 rounded-full object-cover ring-2 ring-gray-100"
                    :src="row.avatar
                        ? `/storage/${row.avatar}`
                        : (row.gender === 'male' ? userMale : userFemale)"
                    :alt="row.first_name"
                />
            </template>

            <!-- Full name -->
            <template #name="{ row }">
                <span class="font-medium text-gray-800">
                    {{ row.first_name }} {{ row.other_name }} {{ row.last_name }}
                </span>
            </template>

            <!-- Current application status -->
            <template #application="{ row }">
                <span
                    v-if="row.current_application"
                    class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize"
                    :class="{
                        'bg-gray-100 text-gray-600':    row.current_application.status === 'draft',
                        'bg-primary-50 text-primary-700':     row.current_application.status === 'submitted',
                        'bg-indigo-50 text-indigo-700': row.current_application.status === 'invited',
                        'bg-purple-50 text-purple-700': row.current_application.status === 'interviewed',
                        'bg-green-50 text-green-700':   row.current_application.status === 'admitted',
                        'bg-red-50 text-red-600':       row.current_application.status === 'rejected',
                        'bg-emerald-50 text-emerald-700': row.current_application.status === 'enrolled',
                    }"
                >
                    {{ row.current_application.status }}
                </span>
                <span v-else class="text-xs text-gray-400">No application</span>
            </template>

            <!-- Active status + toggle -->
            <template #status="{ row }">
                <div class="flex items-center gap-2">
                    <span
                        class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
                        :class="row.is_active
                            ? 'bg-green-50 text-green-700'
                            : 'bg-orange-50 text-orange-600'"
                    >
                        <span class="w-1.5 h-1.5 rounded-full"
                            :class="row.is_active ? 'bg-green-500' : 'bg-orange-400'">
                        </span>
                        {{ row.is_active ? 'Active' : 'Inactive' }}
                    </span>
                    <button
                        @click="toggleActive(row.id, row.is_active)"
                        class="text-gray-400 hover:text-primary transition"
                        :title="row.is_active ? 'Deactivate' : 'Activate'"
                    >
                        <PhToggleRight v-if="row.is_active" :size="22" class="text-green-600" />
                        <PhToggleLeft  v-else                :size="22" class="text-gray-400"  />
                    </button>
                </div>
            </template>
        </Table>
    </Layout>
</template>